import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  "data-testid": "renewal-activity-log",
  class: "overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
}
const _hoisted_2 = { class: "border-b border-slate-200 bg-gradient-to-r from-indigo-50/80 via-white to-white px-5 py-4" }
const _hoisted_3 = { class: "flex items-start justify-between gap-4" }
const _hoisted_4 = { class: "flex min-w-0 items-start gap-3" }
const _hoisted_5 = { class: "flex size-9 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600" }
const _hoisted_6 = {
  key: 0,
  class: "shrink-0 rounded-full bg-white px-2.5 py-1 text-[0.68rem] font-bold text-indigo-600 ring-1 ring-indigo-100"
}
const _hoisted_7 = {
  key: 0,
  "data-testid": "renewal-activity-loading",
  class: "space-y-3 p-5"
}
const _hoisted_8 = {
  key: 1,
  "data-testid": "renewal-activity-unavailable",
  class: "m-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
}
const _hoisted_9 = {
  key: 2,
  "data-testid": "renewal-activity-empty",
  class: "m-5 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-center text-sm text-slate-500"
}
const _hoisted_10 = {
  key: 3,
  "data-testid": "renewal-activity-entries",
  class: "divide-y divide-slate-100"
}
const _hoisted_11 = { class: "flex items-start gap-3" }
const _hoisted_12 = { class: "mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500" }
const _hoisted_13 = { class: "min-w-0 flex-1" }
const _hoisted_14 = { class: "flex flex-wrap items-center justify-between gap-2" }
const _hoisted_15 = { class: "text-sm font-bold text-slate-800" }
const _hoisted_16 = { class: "inline-flex w-fit rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-100" }
const _hoisted_17 = { class: "mt-2 flex flex-wrap items-center gap-2" }
const _hoisted_18 = { class: "inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-1 text-[0.68rem] font-medium text-slate-600" }
const _hoisted_19 = { class: "inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-1 text-[0.68rem] font-medium text-slate-600" }
const _hoisted_20 = {
  key: 0,
  class: "inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-1 text-[0.68rem] font-medium text-slate-600"
}
const _hoisted_21 = {
  key: 0,
  class: "mt-3 rounded-lg border-l-2 border-indigo-300 bg-indigo-50/60 px-3 py-2.5 text-sm leading-relaxed text-slate-600"
}
const _hoisted_22 = {
  key: 1,
  class: "mt-4 border-t border-slate-100 pt-3"
}
const _hoisted_23 = { class: "mt-2 grid gap-2 sm:grid-cols-2" }
const _hoisted_24 = { class: "text-xs font-semibold text-slate-600" }
const _hoisted_25 = { class: "mt-1.5 flex min-w-0 items-center gap-1.5 text-xs" }
const _hoisted_26 = { class: "min-w-0 truncate rounded bg-slate-50 px-1.5 py-1 text-slate-400" }
const _hoisted_27 = { class: "min-w-0 truncate rounded bg-emerald-50 px-1.5 py-1 font-semibold text-emerald-700" }

import { computed } from "vue";
import type { RenewalHistoryEntry } from "@/composables/useRenewalTracking";
import {
  Activity,
  ArrowRight,
  CalendarDays,
  Clock3,
  UserRound,
} from "lucide-vue-next";

interface ChangedValue {
  key: string;
  label: string;
  previous: string;
  current: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'RenewalActivityLog',
  props: {
    history: { default: null },
    isLoading: { type: Boolean, default: false },
    isUnavailable: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props;

const sortedHistory = computed(() => {
  return [...(props.history || [])].sort((left, right) => {
    const rightTime = parseTimestamp(right.created_at);
    const leftTime = parseTimestamp(left.created_at);

    if (rightTime === null && leftTime === null) return 0;
    if (rightTime === null) return -1;
    if (leftTime === null) return 1;
    return rightTime - leftTime;
  });
});

function parseTimestamp(value: string | null | undefined) {
  const timestamp = new Date(String(value || "")).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

function formatAction(value: string | null | undefined) {
  return titleCase(value, "Renewal update");
}

function formatStatus(value: string | null | undefined) {
  return titleCase(value, "Not recorded");
}

function formatTimestamp(value: string | null | undefined) {
  const timestamp = parseTimestamp(value);
  if (timestamp === null) return "Date unavailable";

  return new Date(timestamp).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function actorLabel(entry: RenewalHistoryEntry) {
  if (entry.actor_name) return entry.actor_name;

  const actorType = entry.actor_type
    ? titleCase(entry.actor_type, "System")
    : "System";
  if (entry.actor_id != null) return `${actorType} #${entry.actor_id}`;

  return actorType;
}

function formatEffectiveDate(value: string | null | undefined) {
  const normalized = String(value || "").trim();
  if (!normalized) return "";

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalized);
  const date = dateOnlyMatch
    ? new Date(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3])
      )
    : new Date(normalized);

  if (Number.isNaN(date.getTime())) return normalized;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatStatusTransition(entry: RenewalHistoryEntry) {
  const previous = entry.status_from ? formatStatus(entry.status_from) : "";
  const current = entry.status_to ? formatStatus(entry.status_to) : "";

  if (previous && current) return `${previous} → ${current}`;
  if (current) return `→ ${current}`;
  if (previous) return `${previous} →`;
  return "No status change";
}

function changedValues(entry: RenewalHistoryEntry): ChangedValue[] {
  const previousValues = isRecord(entry.previous_values)
    ? entry.previous_values
    : {};
  const newValues = isRecord(entry.new_values) ? entry.new_values : {};
  const keys = Array.from(
    new Set([...Object.keys(previousValues), ...Object.keys(newValues)])
  );

  return keys
    .filter(
      (key) =>
        serializeValue(previousValues[key]) !== serializeValue(newValues[key])
    )
    .map((key) => ({
      key,
      label: titleCase(key, key),
      previous: formatChangedValue(previousValues[key], key),
      current: formatChangedValue(newValues[key], key),
    }));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function serializeValue(value: unknown) {
  return JSON.stringify(value ?? null);
}

function formatChangedValue(value: unknown, key: string) {
  if (value === null || value === undefined || value === "") return "Not set";

  if (typeof value === "string" && /date/i.test(key)) {
    return formatEffectiveDate(value) || value;
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
}

function titleCase(value: string | null | undefined, fallback: string) {
  const source = String(value || "")
    .replace(/[_-]+/g, " ")
    .trim();
  if (!source) return fallback;

  return source.replace(/\b\w/g, (character) => character.toUpperCase());
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_unref(Activity), {
              class: "size-4",
              "aria-hidden": "true"
            })
          ]),
          _cache[0] || (_cache[0] = _createElementVNode("div", { class: "min-w-0" }, [
            _createElementVNode("h2", { class: "text-sm font-bold text-slate-800" }, " Renewal Activity "),
            _createElementVNode("p", { class: "mt-1 text-xs leading-5 text-slate-500" }, " A chronological record of renewal updates and notes. ")
          ], -1))
        ]),
        (!__props.isLoading && !__props.isUnavailable)
          ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(sortedHistory.value.length) + " event" + _toDisplayString(sortedHistory.value.length === 1 ? "" : "s"), 1))
          : _createCommentVNode("", true)
      ])
    ]),
    (__props.isLoading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (item) => {
            return _createElementVNode("div", {
              key: item,
              class: "h-20 animate-pulse rounded-xl bg-slate-100"
            })
          }), 64))
        ]))
      : (__props.isUnavailable)
        ? (_openBlock(), _createElementBlock("p", _hoisted_8, " Renewal activity is unavailable right now. "))
        : (!sortedHistory.value.length)
          ? (_openBlock(), _createElementBlock("p", _hoisted_9, " No renewal activity has been recorded yet. "))
          : (_openBlock(), _createElementBlock("ol", _hoisted_10, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedHistory.value, (entry) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: entry.id || `${entry.action}-${entry.created_at}`,
                  class: "px-5 py-5 transition-colors hover:bg-slate-50/70"
                }, [
                  _createElementVNode("div", _hoisted_11, [
                    _createElementVNode("div", _hoisted_12, [
                      _createVNode(_unref(Activity), {
                        class: "size-4",
                        "aria-hidden": "true"
                      })
                    ]),
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("div", _hoisted_14, [
                        _createElementVNode("p", _hoisted_15, _toDisplayString(formatAction(entry.action)), 1),
                        _createElementVNode("span", _hoisted_16, _toDisplayString(formatStatusTransition(entry)), 1)
                      ]),
                      _createElementVNode("div", _hoisted_17, [
                        _createElementVNode("span", _hoisted_18, [
                          _createVNode(_unref(Clock3), {
                            class: "size-3.5 text-slate-400",
                            "aria-hidden": "true"
                          }),
                          _createTextVNode(" " + _toDisplayString(formatTimestamp(entry.created_at)), 1)
                        ]),
                        _createElementVNode("span", _hoisted_19, [
                          _createVNode(_unref(UserRound), {
                            class: "size-3.5 text-slate-400",
                            "aria-hidden": "true"
                          }),
                          _createTextVNode(" Updated by: " + _toDisplayString(actorLabel(entry)), 1)
                        ]),
                        (entry.effective_date)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_20, [
                              _createVNode(_unref(CalendarDays), {
                                class: "size-3.5 text-slate-400",
                                "aria-hidden": "true"
                              }),
                              _createTextVNode(" Effective " + _toDisplayString(formatEffectiveDate(entry.effective_date)), 1)
                            ]))
                          : _createCommentVNode("", true)
                      ]),
                      (entry.note)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_21, _toDisplayString(entry.note), 1))
                        : _createCommentVNode("", true),
                      (changedValues(entry).length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                            _cache[1] || (_cache[1] = _createElementVNode("p", { class: "text-[0.68rem] font-bold uppercase tracking-[0.12em] text-slate-400" }, " Changed values ", -1)),
                            _createElementVNode("dl", _hoisted_23, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(changedValues(entry), (change) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: change.key,
                                  class: "rounded-lg border border-slate-200 bg-white px-3 py-2.5"
                                }, [
                                  _createElementVNode("dt", _hoisted_24, _toDisplayString(change.label), 1),
                                  _createElementVNode("dd", _hoisted_25, [
                                    _createElementVNode("span", _hoisted_26, _toDisplayString(change.previous), 1),
                                    _createVNode(_unref(ArrowRight), {
                                      class: "size-3.5 shrink-0 text-slate-300",
                                      "aria-hidden": "true"
                                    }),
                                    _createElementVNode("span", _hoisted_27, _toDisplayString(change.current), 1)
                                  ])
                                ]))
                              }), 128))
                            ])
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ])
                ]))
              }), 128))
            ]))
  ]))
}
}

})